<template>
  <div>
    <v-container>
      <v-row align="center">
        <v-col cols="6">
          <div class="text-h5 ma-2">Relationship Classification Rules
          </div>
        </v-col>
      </v-row>
      <v-divider/>
      <v-row align="center">
        <v-col cols="12">
          <v-data-table
            :headers="headers"
            :items="rules"
            :loading="loading"
            :footer-props="footerProps"
            :hide-default-footer="false"
            item-key="id"
            :custom-sort="customSort"
            fixed-header
            id="relationshipClassificationTable">
            <template v-slot:item="{ item }">
              <tr>
                <td><relationship-classification-rule-dialog :rule="item"
                  @success="onRuleSaved" :existingRules="rules"
                  :classificationMap="relationshipClassifications" isEditButton="true"/></td>
                <td style="width: 30%">
                  {{ relationshipClassifications.get(item.relationshipClassification) }}</td>
                <td style="width: 40%">{{ item.relationshipCodes.join("; ") }}</td>
                <td>
                  <v-btn icon @click="onDeleteSingleRule(item)">
                    <v-icon>fa-trash</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>

            </v-data-table>
          </v-col>
        </v-row>

        <v-row v-if="!loading">
          <v-col>
            <span class="pr-4">
              <relationship-classification-rule-dialog :existingRules="rules"
                :classificationMap="relationshipClassifications"
                @success="onRuleSaved"/>
            </span>
          </v-col>
        </v-row>
    </v-container>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { AxiosResponse } from 'axios';
import {
  Component, Watch, Inject, Provide,
} from 'vue-property-decorator';
import {
  RelationshipClassificationResource, RelationshipClassificationRuleResource,
  RelationshipClassificationRule,
} from '@/api/relationshipclassification/';
import { ConfigVersion } from '@/api/configversion/';
import RelationshipClassificationRuleDialog
  from '@/components/RelationshipClassificationRuleDialog.vue';
import { Alert, NoticeClass, Dialog } from '@kasasa/fbase-components/lib';
import { mapState } from 'vuex';
import { DataTableHeader } from 'vuetify';

@Component({
  computed: mapState([
    'fiId', 'configVersion',
  ]),
  components: {
    'relationship-classification-rule-dialog': RelationshipClassificationRuleDialog,
  },
})
export default class RelationshipClassification extends Vue {
  fiId!:string;

  configVersion!:ConfigVersion;

  relationshipClassifications:Map<string, string> = new Map<string, string>();

  rules:RelationshipClassificationRule[] = [];

  loading:boolean = true;

  headers:DataTableHeader[] = [
    { text: '', value: 'edit' },
    { text: 'Relationship Type', value: 'relationshipClassification' },
    { text: 'Code', value: 'relationshipCodes' },
    { text: '', value: 'menu' },
  ];

  footerProps = {
    'items-per-page-options': [{ value: -1, text: 'All' }, 25, 50, 100],
  }

  @Inject('RelationshipClassificationResource')
  private classificationResource!: RelationshipClassificationResource;

  @Inject('RelationshipClassificationRuleResource')
  ruleResource!:RelationshipClassificationRuleResource;

  @Watch('configVersion')
  mounted() {
    this.loading = false;
    this.classificationResource.getAll()
      .then((result:Map<string, string>) => {
        this.relationshipClassifications = result;
        this.loadRules();
      })
      .catch((error:any) => {
        this.showError(error);
        this.loading = false;
      });
  }

  loadRules() {
    this.rules = [];
    this.loading = true;
    this.ruleResource.getAll(this.fiId)
      .then((result:RelationshipClassificationRule[]) => {
        this.rules = result;
        this.loading = false;
      })
      .catch((error:any) => {
        this.loading = false;
        this.showError(error);
      });
  }

  showError(error:any) {
    if (error.response && error.response.data && error.response.data.entity) {
      this.setError(error.response.data.entity);
    } else {
      this.setError('An unexpected error occurred.');
    }
  }

  setError(message:string) {
    const al = new Alert(message, NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }

  onRuleSaved(savedRule:RelationshipClassificationRule) {
    let found:boolean = false;
    for (let i:number = 0; i < this.rules.length; i++) {
      if (this.rules[i].id === savedRule.id) {
        // Saved an existing rule, so make sure that it is up to date.
        Object.assign(this.rules[i], savedRule);
        found = true;
        break;
      }
    }
    if (!found) {
      const rule = savedRule;
      this.rules.push(rule);
    }
  }

  customSort = (items:RelationshipClassificationRule[], sortBy:string[],
    sortDesc:boolean[]) : RelationshipClassificationRule[] => {
    items.sort((a:any, b:any) => {
      let sortVal:number = 0;
      if (sortBy) {
        for (let i:number = 0; i < sortBy.length; i++) {
          if (a[sortBy[i]] !== b[sortBy[i]]) {
            if (sortDesc[i]) {
              sortVal = a[sortBy[i]] < b[sortBy[i]] ? 1 : -1;
            } else {
              sortVal = a[sortBy[i]] < b[sortBy[i]] ? -1 : 1;
            }
            break;
          } else {
            sortVal = 0;
          }
        }
      }
      return sortVal;
    });
    return items;
  }

  onDeleteSingleRule(rule:RelationshipClassificationRule) {
    const that = this;
    const dialog = new Dialog('Confirm Deletion',
      'Are you sure you want to delete this relationship classification rule?');
    dialog.setAcceptLabel('OK')
      .setDeclineLabel('Cancel');
    this.$store.dispatch('notices/add', dialog)
      .then((ok) => {
        if (ok === 'accept') {
          that.ruleResource.remove(that.fiId, rule.id as number)
            .then((result:AxiosResponse) => {
              that.removeRuleFromList(rule);
            });
        }
      });
  }

  removeRuleFromList(rule:RelationshipClassificationRule) {
    for (let i:number = 0; i < this.rules.length; i++) {
      if (this.rules[i].id === rule.id) {
        this.rules.splice(i, 1);
        break;
      }
    }
  }
}
</script>

<style lang="scss">
tbody tr:nth-of-type(odd) {
   background-color: rgba(31, 25, 25, 0.02);
   background-blend-mode: multiply;
 }

#relationshipClassificationTable
.v-data-table__wrapper{max-height:calc(100vh - 400px) !important;}

</style>
